import type { SimpleChanges } from '@angular/core';
import { Component, Input, ChangeDetectorRef, output, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

import type { Observable } from 'rxjs';
import { Subject, takeUntil } from 'rxjs';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { RequisitionStatus } from '@core/requisitions-types';
import { TimeAgoPipe } from '@core/time-legacy';
import {
  AvatarComponent,
  BadgeComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  ClickToCopyDirective,
  IconButtonComponent,
  InputComponent,
  RtTooltip,
} from '@core/user-interface-legacy';

import type { ApprovalRequestWithRequester } from '../../requisitions.types';
import { RequisitionsStatusBadgeComponent } from '../requisitions-status-badge/requisitions-status-badge.component';

@Component({
  selector: 'rt-requisitions-header',
  templateUrl: './requisitions-header.component.html',
  styleUrls: ['./requisitions-header.component.less'],
  imports: [
    AvatarComponent,
    BadgeComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    ClickToCopyDirective,
    IconButtonComponent,
    InputComponent,
    RequisitionsStatusBadgeComponent,
    RtTooltip,
    TimeAgoPipe,
    TranslatePipe,
    ReactiveFormsModule,
  ],
})
export class RequisitionsHeaderComponent {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly i18n = inject(I18nService);

  @Input() public status: RequisitionStatus;
  @Input() public title: string = '';
  @Input() public guid: string = '';
  @Input() public isEditable: boolean;
  @Input() public isArchived: boolean;
  @Input() public titleSaved$: Observable<void>;
  @Input() public approvalRequest?: ApprovalRequestWithRequester;

  public saveTitle = output<string>();

  public isEditing = false;
  public isSaving = false;
  public requesterFullName: string | null = null;

  public titleControl = new FormControl('', {
    validators: [Validators.required, Validators.maxLength(255)],
    nonNullable: true,
  });

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit() {
    this.titleControl.valueChanges
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => this.cdRef.detectChanges());
    if (this.titleSaved$) {
      this.titleSaved$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
        this.isEditing = false;
        this.isSaving = false;
        this.cdRef.detectChanges();
      });
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.approvalRequest) {
      this.requesterFullName = this.getRequesterFullName();
    }
  }

  public get hasRequesterAvatar(): boolean {
    return !!this.approvalRequest && this.status !== RequisitionStatus.Draft;
  }

  public getRequesterFullName(): string {
    if (!this.approvalRequest?.requester)
      return this.i18n.translate('app.requisitions.flow.deleted_user');

    const { firstName, lastName } = this.approvalRequest.requester;
    return `${firstName} ${lastName}`;
  }

  public saveClick() {
    this.isSaving = true;
    this.cdRef.detectChanges();
    this.saveTitle.emit(this.titleControl.value);
  }

  public editClick() {
    this.titleControl.setValue(this.title);
    this.isEditing = true;
    this.cdRef.detectChanges();
  }

  public cancelClick() {
    this.isEditing = false;
    this.cdRef.detectChanges();
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
